import { Injectable } from '@angular/core';
import {ServerService} from "./ServerService";
import {timeout} from 'rxjs/operators';

export type EstadoEvidenciaCardapio = 'EXTRAIDO' | 'INFERIDO' | 'AUSENTE' | 'CONFLITANTE';
export type TipoSelecaoCardapio = 'SIMPLES' | 'MULTIPLA';
export type TipoCobrancaCardapio = 'SOMA' | 'MEDIA' | 'MAIOR';
export type TipoProdutoCardapio = 'NORMAL' | 'PIZZA' | 'KIT';
export type FinalidadeAdicionalCardapio = 'TAMANHO_PIZZA' | 'QUANTIDADE_KIT' | 'SABORES_KIT' | 'COMPONENTE_KIT';
export type TipoCobrancaPizzaCardapio = 'MAIOR' | 'MEDIA';

export interface EvidenciaCardapioDraft {
  pagina: number;
  trecho: string;
  estado: EstadoEvidenciaCardapio;
}

export interface OpcaoCardapioDraft {
  unidades?: number | null;
  referencia: string;
  referenciaPai: string;
  nome: string | null;
  preco: number | null;
  descricao: string | null;
  codigoPdv: string | null;
  linkImagem: string | null;
  disponivel: boolean | null;
  delivery: boolean | null;
  mesa: boolean | null;
  ordem: number;
  maximoPorOpcao: number | null;
  evidencias: EvidenciaCardapioDraft[];
}

export interface AdicionalCardapioDraft {
  configComponenteKit?: any;
  referencia: string;
  referenciaPai: string;
  nome: string | null;
  tipoSelecao: TipoSelecaoCardapio | null;
  minimo: number | null;
  maximo: number | null;
  permiteRepetir: boolean | null;
  tipoCobranca: TipoCobrancaCardapio | null;
  finalidade: FinalidadeAdicionalCardapio | null;
  multiplicaQuantidadeProduto: boolean | null;
  ordem: number;
  opcoes: OpcaoCardapioDraft[];
  evidencias: EvidenciaCardapioDraft[];
}

export interface ProdutoCardapioDraft {
  configKit?: any;
  referencia: string;
  nome: string | null;
  preco: number | null;
  descricao: string | null;
  codigoPdv: string | null;
  tipoProduto: TipoProdutoCardapio;
  linkImagem: string | null;
  ordem: number;
  adicionais: AdicionalCardapioDraft[];
  evidencias: EvidenciaCardapioDraft[];
  selecionado: boolean;
}

export interface TamanhoPizzaImportacaoDraft {
  nome: string;
  qtdePedacos: number;
  qtdeSabores: number;
}

export interface ConfiguracaoPizzaImportacaoDraft {
  templateId: number | null;
  tipoCobranca: TipoCobrancaPizzaCardapio;
  tamanhos: TamanhoPizzaImportacaoDraft[];
}

export interface CategoriaCardapioDraft {
  referencia: string;
  nome: string | null;
  ordem: number;
  produtos: ProdutoCardapioDraft[];
  evidencias: EvidenciaCardapioDraft[];
}

export interface PendenciaCardapioDraft {
  codigo: string;
  caminho: string;
  mensagem: string;
  bloqueiaImportacao: boolean;
}

export interface CatalogoImportacaoDraft {
  versao: 1;
  configuracaoPizza: ConfiguracaoPizzaImportacaoDraft | null;
  categorias: CategoriaCardapioDraft[];
  pendencias: PendenciaCardapioDraft[];
  estatisticas: {
    categorias: number;
    produtos: number;
    adicionais: number;
    opcoes: number;
    pendenciasBloqueantes: number;
  };
}

export interface ResultadoExtracaoCardapio {
  rascunho: CatalogoImportacaoDraft;
  modelo: string;
  uso: {
    tokensEntrada: number;
    tokensSaida: number;
    // custo estimado da chamada em dólar; null quando o servidor não conhece o preço do modelo
    custoDolar: number | null;
  };
}

export interface EstadoAnaliseCardapioIa {
  analiseId: string;
  estado: 'ENFILEIRADA' | 'PROCESSANDO' | 'CONCLUIDA' | 'FALHA';
  progresso: {produtos: number; categorias: number; tokensSaida: number} | null;
  resultado: ResultadoExtracaoCardapio | null;
  erro: string | null;
  prazoFinal: number;
}

export interface FalhaImportacaoCardapioIA {
  referencia: string;
  nome: string | null;
  mensagem: string;
}

export interface ResultadoImportacaoCardapioIA {
  importados: string[];
  falhas: FalhaImportacaoCardapioIA[];
  categoriasCriadas: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImportadorCardapioIaService extends ServerService {

  // idEmpresa: só admin/superadmin pode importar em outra loja (wizard de nova empresa no superadmin)
  analise(arquivos: File[], idEmpresa: number = null, analiseId: string = null): Promise<{analiseId: string}> {
    const formData = new FormData();

    // um PDF ou várias fotos: sempre no campo file (repetido vira array no servidor)
    arquivos.forEach((arquivo) => formData.append('file', arquivo, arquivo.name));
    if (idEmpresa) formData.append('idEmpresa', String(idEmpresa));
    if (analiseId) formData.append('analiseId', analiseId);

    return this.http.post('/importador/cardapio-ia/analisar', formData)
      .pipe(timeout(60000)).toPromise().then(this.retorno);
  }

  // texto colado do cardápio: vai no mesmo campo file, como text/plain, e o servidor trata como fonte única
  analiseTexto(texto: string, idEmpresa: number = null, analiseId: string = null): Promise<{analiseId: string}> {
    const formData = new FormData();

    formData.append('file', new Blob([texto], {type: 'text/plain'}), 'cardapio.txt');
    if (idEmpresa) formData.append('idEmpresa', String(idEmpresa));
    if (analiseId) formData.append('analiseId', analiseId);

    return this.http.post('/importador/cardapio-ia/analisar', formData)
      .pipe(timeout(60000)).toPromise().then(this.retorno);
  }

  consulteAnalise(analiseId: string, idEmpresa: number = null): Promise<EstadoAnaliseCardapioIa> {
    // Preserva o status HTTP para a tela distinguir falha transitória de tarefa expirada.
    return this.http.get(`/importador/cardapio-ia/analises/${encodeURIComponent(analiseId)}`, {
      params: idEmpresa ? {idEmpresa: String(idEmpresa)} : {}
    }).pipe(timeout(10000)).toPromise().then(this.retorno);
  }

  importe(rascunho: CatalogoImportacaoDraft, referencias: string[], idEmpresa: number = null): Promise<ResultadoImportacaoCardapioIA> {
    const corpo: any = {rascunho, referencias};
    if (idEmpresa) corpo.idEmpresa = idEmpresa;
    return this.facaPost('/importador/cardapio-ia/importar', corpo);
  }
}
